import type { Namespace, TFunction } from 'i18next';
import { generateSearchParamsForCustomerInsightsNormalisedCategory } from '../../../lib/search-params-helpers.ts';
import type { InsightDTO } from '../../../server/dto/insight-dto.ts';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { getInsightEntityName } from './insight-entity-name.ts';
import { newCategoryInsightTypes } from './new-category-insight-types.ts';

export function buildCustomerNormalisedCategoryLink(
    customerId: string,
    categoryId: string,
    categoryName: string,
): string {
    const params = generateSearchParamsForCustomerInsightsNormalisedCategory(categoryId, categoryName);
    return `/customers/${encodeURIComponent(customerId)}/insights?${params}`;
}

function shouldShowInsightCategoryLink(insight: InsightDTO): boolean {
    const isWebCategoryInsight = insight.actionable_entity_type === 'WEB_CATEGORY';
    const categoryId = insight.actionable_entity_id;

    const shouldShowLink =
        isWebCategoryInsight && !!categoryId && !newCategoryInsightTypes.includes(insight.insight_type);

    return shouldShowLink;
}

export type InsightBreadcrumb = {
    id: string;
    title: string;
    href: string | undefined;
};

export function getInsightCategoryCrumb(
    t: TFunction<Namespace>,
    insightItem: InsightListItem,
): InsightBreadcrumb {
    const { insight, details } = insightItem;

    let href: string | undefined = undefined;

    if (shouldShowInsightCategoryLink(insight)) {
        const categoryId = insight.actionable_entity_id;

        href = buildCustomerNormalisedCategoryLink(
            insight.customer_id,
            categoryId,
            details.category?.title ?? insight.actionable_entity_id,
        );
    }

    return {
        id: details.category?.id ?? insight.actionable_entity_id,
        title: getInsightEntityName(t, insightItem),
        href,
    };
}

export function getInsightBreadcrumbTrail(
    t: TFunction<Namespace>,
    insightItem: InsightListItem,
    breadcrumbDepthsToHide: number,
): InsightBreadcrumb[] {
    const { insight, details } = insightItem;
    const { category } = details;

    if (!category) {
        return [];
    }

    const ancestors = (category.breadcrumbs?.slice(breadcrumbDepthsToHide) ?? []).map((crumb) => ({
        id: crumb.id,
        title: crumb.title,
        href: buildCustomerNormalisedCategoryLink(insight.customer_id, crumb.id, crumb.title),
    }));

    return [...ancestors, getInsightCategoryCrumb(t, insightItem)];
}
